import { ReactNode } from "react";

import { css } from "@compiled/react";

import { AtlassianLogo } from "@atlaskit/logo";
import { media } from "@atlaskit/primitives/responsive";
import { B300, N800 } from "@atlaskit/theme/colors";

import { fontSize } from "@/components/signup/utils";

const signupModule = css({
    alignItems: "center",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    fontFamily: "var(--atlassian-recommended-system-fonts)",
    fontSize: fontSize.sm,
    justifyContent: "space-between",
    padding: "1.5rem 1rem"
});

const fullHeightSignupModule = css({
    minHeight: "80vh",

    [media.above.sm]: {
        minHeight: "95vh"
    }
});

const fullPageSignupModule = css({
    padding: 0,

    [media.above.xs]: {
        minHeight: "auto"
    },

    [media.above.sm]: {
        minHeight: "auto"
    }
});

const container = css({
    "flex": 1,
    "maxWidth": "25rem",
    "width": "100%",

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "h1, h2": {
        fontFamily: "var(--atlassian-recommended-system-fonts)", // needed to override index.css
        textAlign: "center"
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "h1": {
        fontSize: fontSize.xl,
        fontWeight: 600,
        lineHeight: 1.15,
        margin: 0,
        [media.above.sm]: {
            margin: "0 -3rem" // "sm" is 48rem, maxWidth is 25rem (> 3rem on each side)
        }
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "h2": {
        "fontSize": "inherit",
        "fontWeight": "normal",
        "margin": "0.5rem 0 0.5rem",

        "@media (min-width: 376px)": {
            marginBlockEnd: "1.5rem"
        }
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "h2, p, label, small": {
        color: N800
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "p": {
        lineHeight: 1.5,
        margin: "1rem 0"
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "label, small": {
        fontSize: fontSize.xs
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "a": {
        "color": B300,

        "&:hover, &:focus": {
            textDecoration: "underline"
        }
    }
});

const fullPageContainer = css({
    maxWidth: "100%",

    [media.above.sm]: {
        marginBlockStart: 0
    },

    [media.above.md]: {
        marginBlockStart: 0
    }
});

const footer = css({
    margin: "1.5rem auto"
});

export interface SignupModuleWrapperProps {
    fullPage?: boolean;
    fullHeight?: boolean;
    hideFooter?: boolean;
    children: ReactNode;
}

export const SignupModuleWrapper = ({
    fullPage,
    fullHeight = true,
    hideFooter,
    children
}: SignupModuleWrapperProps) => {
    return (
        <div
            css={[
                signupModule,
                fullPage && fullPageSignupModule,
                fullHeight && fullHeightSignupModule
            ]}
        >
            <div css={[container, fullPage && fullPageContainer]}>
                {children}
            </div>
            {!hideFooter && (
                <div css={footer}>
                    <AtlassianLogo
                        appearance="brand"
                        size="small"
                        testId="atlassian-logo"
                        label="Atlassian logo"
                    />
                </div>
            )}
        </div>
    );
};
